import {
  ApiOrdersPlaceOrderResult,
  ApiOrdersPlaceOrdersFailedAction,
  ApiOrdersPlaceOrdersSucceededAction,
  apiOrdersPlaceOrdersFailedType,
  apiOrdersPlaceOrdersRequested,
  apiOrdersPlaceOrdersSucceededType
} from '@spa/lib/store/api/orders/placeOrdersApi';
import { OptionalStateCarts, PlaceCartResult, StateCart } from '@spa/lib/store/carts/CartsState';
import {
  PlaceCurrentCartsRequestedAction,
  placeCurrentCartsFailed,
  placeCurrentCartsSucceeded
} from '../../carts/cartsSlice';
import { put, select } from 'typed-redux-saga';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import { clearAgeVerifiedStorage } from '@spa/components/checkout/checkout-summary/ageVerifiedStorage';
import convertIfNotUndefinedOrNull from '@spa/lib/converters/convertIfNotUndefinedOrNull';
import generateActionId from '@spa/lib/generateActionId';
import selectAuthenticatedUserId from '@spa/lib/store/auth/selectors/selectAuthenticatedUserId';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import selectSessionUserId from '@spa/lib/store/session/selectors/selectSessionUserId';
import { trackSuccessfulCheckout } from '@spa/components/checkout/checkout-place-order/checkout-tracking';
import waitForSucceededOrFailedAction from '@spa/lib/store/sagas/session/waitForSucceededOrFailedAction';

const convertResult = (
  { orderId, action, error, giftCardCancellation, validationErrors }: ApiOrdersPlaceOrderResult,
  cartId: string | null
): PlaceCartResult => {
  let target: PlaceCartResult;
  if (orderId === null) {
    target = {
      succeeded: false,
      action,
      error: error ?? '',
      giftCardCancellation,
      ...(validationErrors?.length && { validationErrors })
    };
  } else {
    if (cartId === null) {
      throw new Error("Cart id can't be null");
    }
    target = {
      succeeded: true,
      orderId,
      cartId
    };
  }

  return target;
};

const getCartIfNotEmpty = (
  carts: OptionalStateCarts,
  customOrderType: CustomOrderType
): StateCart | null => {
  const cart: StateCart | null = carts[customOrderType] ?? null;
  return cart !== null && cart.entries.length > 0 ? cart : null;
};

const trackCheckout = (cart: StateCart, anonymousUserId: string, userId: string | null): void => {
  const deliveryAddress = cart.deliveryAddress;
  trackSuccessfulCheckout(userId, anonymousUserId, {
    country: deliveryAddress?.countryIsocode,
    city: deliveryAddress?.town,
    zip: deliveryAddress?.postalCode
  });
};

function* placeCurrentCartsSaga({
  payload: { actionId, additionalData, emarsysParams }
}: PlaceCurrentCartsRequestedAction): Generator {
  const carts: OptionalStateCarts = yield* select(selectCarts);

  const casCart: StateCart | null = getCartIfNotEmpty(carts, CustomOrderType.COLLECT_AT_STORE);
  const casCartId: string | null = casCart?.code ?? null;
  const homeCart: StateCart | null = getCartIfNotEmpty(carts, CustomOrderType.HOME_DELIVERY);
  const homeCartId: string | null = homeCart?.code ?? null;

  let casResult: PlaceCartResult | null = null;
  let homeResult: PlaceCartResult | null = null;

  if (casCartId !== null || homeCartId !== null) {
    const apiPlaceOrdersRequestedActionId = generateActionId();
    yield* put(
      apiOrdersPlaceOrdersRequested({
        actionId: apiPlaceOrdersRequestedActionId,
        casCartId,
        homeCartId,
        parentActionId: actionId,
        additionalData,
        emarsysParams
      })
    );

    const waitResult = yield* waitForSucceededOrFailedAction<
      typeof apiOrdersPlaceOrdersSucceededType,
      typeof apiOrdersPlaceOrdersFailedType,
      ApiOrdersPlaceOrdersSucceededAction,
      ApiOrdersPlaceOrdersFailedAction
    >(
      apiPlaceOrdersRequestedActionId,
      apiOrdersPlaceOrdersSucceededType,
      apiOrdersPlaceOrdersFailedType
    );

    if (waitResult.type === 'succeeded') {
      const payload = waitResult.action.payload;
      casResult = convertIfNotUndefinedOrNull(payload.casResult, convertResult, casCartId);
      homeResult = convertIfNotUndefinedOrNull(payload.homeResult, convertResult, homeCartId);
    }
  }

  if (
    (casResult === null && homeResult === null) ||
    casResult?.succeeded === false ||
    homeResult?.succeeded === false
  ) {
    yield* put(
      placeCurrentCartsFailed({
        requestedActionId: actionId,
        homeDelivery: homeResult,
        collectAtStore: casResult
      })
    );
  } else {
    clearAgeVerifiedStorage();
    yield* put(
      placeCurrentCartsSucceeded({
        requestedActionId: actionId,
        homeDelivery: homeResult,
        collectAtStore: casResult
      })
    );
  }

  const anonymousUserId: string = yield* select(selectSessionUserId);
  const userId: string | null = yield* select(selectAuthenticatedUserId);

  if (casCart !== null && casResult?.succeeded === true) {
    trackCheckout(casCart, anonymousUserId, userId);
  }
  if (homeCart !== null && homeResult?.succeeded === true) {
    trackCheckout(homeCart, anonymousUserId, userId);
  }
}

export default placeCurrentCartsSaga;
